/**
 * This file tracks events specific to product.
 */
import {makeTrackEvent} from './makeTrackEvent';
import {ActivationMethods, NoEventData} from './types';

export const enum ProdEventNames {
  AdBlockerStatus = 'ad-blocker-status',
  AppLauncherFailure = 'app-launcher-failure', // tracks non-executable app launcher attempts
  AppLauncherUsed = 'app-launcher-used', // tracks each time the app launcher is used to navigate
  AppLauncherOnScreen = 'app-launcher-on-screen', // tracks each time the app launcher is triggered
  RunsAggregationToggled = 'Runs aggregation toggled', // tracks each time the user toggles the runs aggregation
  RunGroupNodeViewed = 'Run Group Node Viewed', // tracks each time a user views a run group node in the DAG
  SearchNavOnScreen = 'search-nav-on-screen', // tracks each time the search nav is triggered
  SearchNavUsed = 'search-nav-used', // tracks each time the search nav is used to navigate
  TempViewNavigation = 'Temporary Workspace View Bar Navigation Triggered', // tracks each time the user navigates to a temp view
}

export type QuickNavData = {
  activationMethod: ActivationMethods;
  destination: string;
  location: string;
  type: 'option-selected' | 'direct-navigation';
};

export type QuickNavDescriptions = {
  activationMethod: {
    description: 'Whether the action was triggered by keyboard shortcut or mouse';
    exampleValues: [
      ActivationMethods.MouseClick,
      ActivationMethods.KeyboardHotkey
    ];
  };
  destination: {
    description: 'The new app location being navigated to';
    exampleValues: ['/home', '/[entityName]'];
  };
  location: {
    description: 'The current location within the application';
    exampleValues: ['/home', '/[entityName]'];
  };
  type: {
    description: 'This determines if a user found a matching option in the results list or if they successfully navigated to a location by typing the URL directly';
    exampleValues: ['option-selected', 'direct-navigation'];
  };
};

type AdBlockerEventData = {
  isActive: 'true' | 'false' | 'unknown';
};
type AdBlockerEventDataDescriptions = {
  isActive: {
    description: 'If a user has an ad-blocker enabled (tested by blocking Pendo tracking pixel). Three possible states to be able to quantify conditions where a seam in our check might exist.';
    exampleValues: ['true', 'false', 'unknown'];
  };
};

export const checkIsAdBlockerActive = makeTrackEvent<
  AdBlockerEventData,
  AdBlockerEventDataDescriptions & {
    _description: 'A user has an ad blocker active';
    _location: 'Happens on app initialization';
    _motivation: 'Ad blockers interfere with displaying user guides from Pendo or accurately gauging user sentiment with NPS surveys';
  }
>(ProdEventNames.AdBlockerStatus);

export const withinLastFilterAdded = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires once when the user adds the "within last" op to a filter';
    _location: 'Workspace > edit filters UI';
    _motivation: "We want to track usage of this new feature we've added";
  }
>('User added Within Last Filter');
export const withinLastFilterRemoved = makeTrackEvent<
  NoEventData,
  {
    _description: 'Fires once when the user removes the "within last" op from a filter';
    _location: 'Workspace > edit filters UI';
    _motivation: "We want to track usage of this new feature we've added";
  }
>('User removed Within Last Filter');

export const appLauncherFailure = makeTrackEvent<
  Pick<QuickNavData, 'destination' | 'location'>,
  QuickNavDescriptions & {
    _description: 'A user has tried to enter a search term in the launcher nav that does not link to a destination';
    _location: 'Global - App launcher bar';
    _motivation: 'Identify destinations users desire but we do not support';
  }
>(ProdEventNames.AppLauncherFailure);

export const appLauncherUsed = makeTrackEvent<
  Omit<QuickNavData, 'activationMethod'>,
  QuickNavDescriptions & {
    _description: 'A successful entry in the app launcher leading to a new destination';
    _location: 'Global - App launcher bar';
    _motivation: 'Identify where users quickly navigate to';
  }
>(ProdEventNames.AppLauncherUsed);

export const appLauncherOnScreen = makeTrackEvent<
  Pick<QuickNavData, 'activationMethod' | 'location'>,
  QuickNavDescriptions & {
    _description: 'The app launcher widget has become visible in the app';
    _location: 'Global - App launcher bar';
    _motivation: 'Track usage / ease of discovery';
  }
>(ProdEventNames.AppLauncherOnScreen);

export const searchNavOnScreen = makeTrackEvent<
  Pick<QuickNavData, 'activationMethod' | 'location'>,
  QuickNavDescriptions & {
    _description: 'The search nav dropdown is being used';
    _location: 'Global - Header nav search input';
    _motivation: 'Track usage of search in header nav';
  }
>(ProdEventNames.SearchNavOnScreen);

export const searchNavUsed = makeTrackEvent<
  Pick<QuickNavData, 'destination' | 'location'>,
  QuickNavDescriptions & {
    _description: 'A successful entry in the search nav leading to a new destination';
    _location: 'Global - Header nav search input';
    _motivation: 'Identify where users quickly navigate to';
  }
>(ProdEventNames.SearchNavUsed);

export const pointCloudViewed = makeTrackEvent<
  {
    type: 'fullscreen' | 'preview';
  },
  {
    type: {
      description: 'is the point cloud being viewed inside of the workspace panel as a preview, or has the user clicked to view the pointcloud fullscreen?';
      exampleValues: ['fullscreen', 'preview'];
    };
  } & {
    _description: 'fired for each instance of a point cloud - eg if 5 point cloud previews are rendered (visible in any part of html then this will fire 5 times. Full screen will fire once per time the user clicks to open full screen mode';
    _location: 'Workspaces > Media Panel > Point Cloud viewer';
    _motivation: 'How many folks are using the point cloud viewer?';
  }
>('3D Point Cloud Viewed');

export const pointCloudFileWorkaroundUsed = makeTrackEvent<
  {},
  {
    _description: 'There was a bug in the way the SDK was submitting files, this tracks when we have to use the workaround to view those files correctly. See detailed discussion in https://github.com/wandb/wandb/pull/4353';
    _location: 'Object3DCard';
    _motivation: 'Do we even need to keep this workaround in the code?';
  }
>('3D Point Cloud Viewed, required workaround');

export const toggleRunAggregation = makeTrackEvent<
  {
    entityName: string;
    projectName: string;
    workspaceId: string | number;
  },
  {
    _description: 'A user has toggled the aggregation of runs in the runs table';
    _location: 'Multirun workspace settings';
    _motivation: 'Track usage of this feature';
    entityName: {
      description: 'The entity currently viewing the workspace';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    workspaceId: {
      description: 'The workspace being viewed';
      exampleValues: ['12asdf34'];
    };
  }
>(ProdEventNames.RunsAggregationToggled);

export const viewRunGroupNode = makeTrackEvent<
  {
    entityName: string;
    projectName: string;
    artifactCollectionName: string;
    artifactCommitHash: string;
    artifactTab: string;
    artifactTypeName: string;
    numRuns: number;
  },
  {
    _description: 'A user has clicked on a run group node in DAG view';
    _location: 'Simple lineage view in the artifact DAG';
    _motivation: 'Track usage of this feature';
    entityName: {
      description: 'The entity currently viewing the workspace';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    artifactCollectionName: {
      description: 'The artifact being viewed';
      exampleValues: ['MNIST_test'];
    };
    artifactCommitHash: {
      description: 'The hash of the artifact';
      exampleValues: ['v0'];
    };
    artifactTab: {
      description: 'The active tab in the artifact view';
      exampleValues: ['lineage'];
    };
    artifactTypeName: {
      description: 'The type of artifact being viewed';
      exampleValues: ['dataset'];
    };
    numRuns: {
      description: 'The number of runs in the run group';
      exampleValues: [5];
    };
  }
>(ProdEventNames.RunGroupNodeViewed);

export const navigateFromTempViewBar = makeTrackEvent<
  {
    entityName: string;
    projectName: string;
    destination: string;
    destinationTab: 'Workspace' | 'Artifacts';
    viewTime: number;
  },
  {
    _description: 'A user has clicked on a link in the temporary view bar';
    _location: 'View bar on temporary workspace page';
    _motivation: 'Track where users are navigating when leaving a temporary worksapce';
    entityName: {
      description: 'The entity currently viewing the workspace';
      exampleValues: ['my-org', 'my-user'];
    };
    projectName: {
      description: 'The project being viewed';
      exampleValues: ['my-project'];
    };
    destination: {
      description: 'The new app location being navigated to';
      exampleValues: ['/:entity/:project', '/:entity/:project/artifacts'];
    };
    destinationTab: {
      description: 'What tab in the project the user is navigating to';
      exampleValues: ['Workspace', 'Artifacts'];
    };
    viewTime: {
      description: 'How long the user was viewing the temporary workspace';
      exampleValues: [1000, 50];
    };
  }
>(ProdEventNames.TempViewNavigation);

export const trackPageMaxLongTask = makeTrackEvent<
  {duration: number},
  {
    _description: `Tracks the page's highest value for the longtask PerformanceObserver reported event. See detailed discussion of what a longtask is at https://developer.mozilla.org/en-US/docs/Web/API/PerformanceLongTaskTiming.`;
    _location: 'All pages on the site';
    _motivation: 'Used for tracking performance on the site';
    duration: {
      description: 'The duration of the longtask in milliseconds';
      exampleValues: [0, 1000];
    };
  }
>('Page longtask max value millis');

export const trackPageTti = makeTrackEvent<
  {duration: number},
  {
    _description: `Tracks the page's Time To Interactive value. See detailed discussion describing TTI: https://github.com/wandb/core/blob/master/frontends/app/weave-ui/common/src/util/profiler/timeToInteractive.ts.`;
    _location: 'All pages on the site';
    _motivation: 'Used for tracking performance on the site';
    duration: {
      description: `What was the page's TTI? Measured in milliseconds`;
      exampleValues: [0, 1000];
    };
  }
>('Page TTI');
